import { db } from '../src/firebase';
import { collection, doc, getDocs, setDoc, deleteDoc, query, where, limit } from 'firebase/firestore';
import type { BlogPost } from '../types';
import { STATIC_SEO_BLOG_POSTS } from '../contexts/blogPostsData';

const BLOG_COLLECTION = 'blogPosts';

const mergeWithStaticBlogPosts = (firestorePosts: BlogPost[]): BlogPost[] => {
  const bySlug = new Map<string, BlogPost>();

  // First load all 100+ static SEO blog posts
  for (const staticPost of STATIC_SEO_BLOG_POSTS) {
    bySlug.set(staticPost.slug, staticPost);
  }

  // Override or add any custom/edited posts from Firestore
  for (const dbPost of firestorePosts) {
    if (dbPost && dbPost.slug) {
      bySlug.set(dbPost.slug, dbPost);
    }
  }

  return Array.from(bySlug.values());
};

export const getBlogPosts = async (onlyPublished = true): Promise<BlogPost[]> => {
  if (typeof window !== 'undefined' && (window as any).__PRERENDER_DATA__) {
    const allData = (window as any).__PRERENDER_DATA__;
    if (allData.allBlogPosts) {
      let posts = mergeWithStaticBlogPosts([...allData.allBlogPosts] as BlogPost[]);
      if (onlyPublished) {
        posts = posts.filter(p => p.status === 'published');
        posts.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
      } else {
        posts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      }
      return posts;
    }
  }

  try {
    const q = query(collection(db, BLOG_COLLECTION));
    const snapshot = await getDocs(q);
    const dbPosts = snapshot.docs.map(d => d.data() as BlogPost);
    let posts = mergeWithStaticBlogPosts(dbPosts);

    if (onlyPublished) {
      posts = posts.filter(p => p.status === 'published');
      posts.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
    } else {
      posts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
    return posts;
  } catch (error) {
    console.error('Error fetching blog posts from Firestore, serving static SEO blog posts:', error);
    let posts = [...STATIC_SEO_BLOG_POSTS];
    if (onlyPublished) {
      posts = posts.filter(p => p.status === 'published');
      posts.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
    }
    return posts;
  }
};

export const getBlogPostBySlug = async (slug: string): Promise<BlogPost | null> => {
  const normalizedSlug = slug.replace(/^\/+|\/+$/g, '');

  if (typeof window !== 'undefined' && (window as any).__PRERENDER_DATA__) {
    const allData = (window as any).__PRERENDER_DATA__;
    const post = allData[`blogPost_${normalizedSlug}`];
    if (post) {
      return post as BlogPost;
    }
    if (allData.allBlogPosts) {
      const found = (allData.allBlogPosts as BlogPost[]).find(p => p.slug === normalizedSlug);
      if (found) return found;
    }
  }

  try {
    const q = query(collection(db, BLOG_COLLECTION), where('slug', '==', normalizedSlug), limit(1));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      return snapshot.docs[0].data() as BlogPost;
    }
  } catch (error) {
    console.error('Error fetching blog post by slug from Firestore:', error);
  }

  const staticMatch = STATIC_SEO_BLOG_POSTS.find(p => p.slug === normalizedSlug);
  return staticMatch || null;
};

export const getBlogPostsByCategory = async (category: string): Promise<BlogPost[]> => {
  const allPublished = await getBlogPosts(true);
  return allPublished.filter(p =>
    p.categories.some(c => c.toLowerCase() === category.toLowerCase())
  );
};

export const getBlogPostsByTag = async (tag: string): Promise<BlogPost[]> => {
  const allPublished = await getBlogPosts(true);
  return allPublished.filter(p =>
    p.tags.some(t => t.toLowerCase() === tag.toLowerCase())
  );
};

export const saveBlogPost = async (post: BlogPost): Promise<boolean> => {
  try {
    await setDoc(doc(db, BLOG_COLLECTION, post.id), post);
    return true;
  } catch (error) {
    console.error('Error saving blog post:', error);
    return false;
  }
};

export const deleteBlogPost = async (id: string): Promise<boolean> => {
  try {
    await deleteDoc(doc(db, BLOG_COLLECTION, id));
    return true;
  } catch (error) {
    console.error('Error deleting blog post:', error);
    return false;
  }
};
